import {
  type Action,
  FilterActionTypes,
  type Thunk,
  action,
  thunk,
} from "easy-peasy";
import isEqual from "lodash/isEqual";

import sortRestaurantsByDistance from "./sortRestaurantsByDistance";
import { Logger } from "../../../dependencies/logger";
import getAllRestaurantIds, {
  type GetAllRestaurantIdsParams,
  type MapRestaurantPreview,
} from "../../../gql/getters/allRestaurantIds";
import {
  AbortError,
  type LogEffect,
  createLogEffect,
} from "../../../utils/store";
import {
  beginListInitialLoad,
  cancelListInitialLoad,
  failListInitialLoad,
} from "../../../utils/store/listPerformance";
import type { LocationCoordinates } from "../../nearTo/model";

const logEffect = (
  property: keyof FilterActionTypes<AllIdsModel>,
  enabled = false,
) => createLogEffect<AllIdsModel>("ALL_IDS", property, enabled);

const logger = new Logger("RESTAURANT_STORE - ALL_IDS", "LOG");
// logger.enable();

export type AllIdsFetchParams = {
  variables: GetAllRestaurantIdsParams["variables"];
  /** User origin for client-side nearest sort. Omit when not sorting by distance. */
  origin?: LocationCoordinates | null;
};

export type AllIdsModel = {
  value: number[];
  valueOnChange: LogEffect<AllIdsModel>;
  set: Action<AllIdsModel, number[]>;

  /**
   * Last GraphQL result, in query order (Featured when the user chose Nearest).
   * Client-side distance sort always reads this so leaving Nearest can restore
   * query order without refetching.
   */
  fetchedPreviews: MapRestaurantPreview[];
  setFetchedPreviews: Action<AllIdsModel, MapRestaurantPreview[]>;

  /** Display order: query order, or distance order when {@link origin} is set. */
  previews: MapRestaurantPreview[];
  setPreviews: Action<AllIdsModel, MapRestaurantPreview[]>;

  resetState: Action<AllIdsModel>;
  /** Abort any in-flight fetch, then clear cached ids and pin previews. */
  reset: Thunk<AllIdsModel>;

  variables?: GetAllRestaurantIdsParams["variables"];
  setVariables: Action<
    AllIdsModel,
    GetAllRestaurantIdsParams["variables"] | undefined
  >;

  /**
   * User coordinates for client-side nearest sort, or `null` when the list is
   * not ordered by distance. Not sent to GraphQL — Nearest reuses the Featured
   * query and reorders {@link fetchedPreviews} locally.
   */
  origin: LocationCoordinates | null;
  setOrigin: Action<AllIdsModel, LocationCoordinates | null>;

  loading: boolean;
  loadingOnChange: LogEffect<AllIdsModel>;
  setLoading: Action<AllIdsModel, boolean>;

  /**
   * Whether `value` is an answer or still a placeholder. Without it, "not
   * fetched yet" and "fetched, no matches" are indistinguishable — both are an
   * empty `value` with `loading` false.
   */
  hasSettled: boolean;
  setHasSettled: Action<AllIdsModel, boolean>;

  error: Error | null;
  setError: Action<AllIdsModel, Error | null>;

  abortController?: AbortController;
  setAbortController: Action<AllIdsModel, AbortController | undefined>;
  abort: Thunk<AllIdsModel, void, unknown, { deriveCacheIdentity: object }>;

  fetch: Thunk<
    AllIdsModel,
    AllIdsFetchParams,
    unknown,
    { mode: "single" | "list" | "map" | undefined; deriveCacheIdentity: object }
  >;
  refetch: Thunk<AllIdsModel>;
};

const applyDistanceOrder = (
  actions: {
    set: (ids: number[]) => void;
    setPreviews: (previews: MapRestaurantPreview[]) => void;
  },
  fetchedPreviews: MapRestaurantPreview[],
  origin: LocationCoordinates | null,
) => {
  const sorted = sortRestaurantsByDistance(fetchedPreviews, origin);
  actions.set(sorted.map((restaurant) => restaurant.id));
  actions.setPreviews(sorted);
};

const allIdsModel: AllIdsModel = {
  value: [],
  valueOnChange: logEffect("value"),
  set: action((state, payload) => {
    logger.log("set", payload);
    state.value = payload;
    // Set here rather than in `fetch` so the empty-favourites short-circuit,
    // which assigns ids without fetching, settles too.
    state.hasSettled = true;
  }),

  fetchedPreviews: [],
  setFetchedPreviews: action((state, payload) => {
    state.fetchedPreviews = payload;
  }),

  previews: [],
  setPreviews: action((state, payload) => {
    state.previews = payload;
  }),

  resetState: action((state) => {
    logger.log("reset");
    state.value = [];
    state.fetchedPreviews = [];
    state.previews = [];
    state.variables = undefined;
    state.origin = null;
    state.loading = false;
    state.hasSettled = false;
    state.error = null;
  }),
  reset: thunk((actions) => {
    actions.abort();
    actions.resetState();
  }),
  variables: undefined,
  setVariables: action((state, payload) => {
    state.variables = payload;
  }),

  origin: null,
  setOrigin: action((state, payload) => {
    state.origin = payload;
  }),

  loading: false,
  loadingOnChange: logEffect("loading"),
  setLoading: action((state, payload) => {
    state.loading = payload;
  }),

  hasSettled: false,
  setHasSettled: action((state, payload) => {
    state.hasSettled = payload;
  }),

  error: null,
  setError: action((state, payload) => {
    state.error = payload;
  }),

  abortController: undefined,
  setAbortController: action((state, abortController) => {
    state.abortController = abortController;
  }),
  abort: thunk((_actions, _payload, { getState, getStoreState }) => {
    cancelListInitialLoad(getStoreState().deriveCacheIdentity);
    const abortController = getState().abortController;
    if (abortController !== undefined && !abortController.signal.aborted) {
      abortController.abort(
        new AbortError('Canceled "allIds.fetch" before re-fetching'),
      );
    }
  }),

  fetch: thunk(
    async (actions, { variables, origin }, { getState, getStoreState }) => {
      const nextOrigin = origin ?? null;
      const current = getState();
      const sameVariables = isEqual(current.variables, variables);

      actions.setOrigin(nextOrigin);

      // Same GraphQL query and a new origin: re-order cache. Same origin still
      // falls through so `refetch` can hit the network. Reordering is not a new
      // list load, so it must not start the clock.
      if (
        sameVariables &&
        !current.loading &&
        current.hasSettled &&
        !isEqual(current.origin, nextOrigin)
      ) {
        applyDistanceOrder(actions, current.fetchedPreviews, nextOrigin);
        return;
      }

      if (sameVariables && current.loading) {
        return;
      }

      actions.abort();
      const abortController = new AbortController();
      actions.setAbortController(abortController);
      const { signal: abortSignal } = abortController;

      if (abortSignal.aborted) {
        return;
      }

      const store = getStoreState().deriveCacheIdentity;

      // Map refetches use this thunk too. Switching into list mode opens its own
      // attempt, so a map fetch must not start the clock.
      if (getStoreState().mode === "list") {
        beginListInitialLoad(store);
      }
      actions.setLoading(true);
      actions.setError(null);
      actions.setVariables(variables);
      logger.log("Fetching...", { variables });

      let aborted = false;
      function onAbort() {
        logger.log(`Fetch aborted: "${abortSignal.reason}"`, { variables });
        aborted = true;
        actions.setLoading(false);
      }
      abortSignal.addEventListener("abort", onAbort);

      try {
        const restaurants = await getAllRestaurantIds(variables, abortSignal);

        if (abortSignal.aborted || aborted) {
          logger.log("Fetch aborted", { variables });
          actions.setLoading(false);
          return;
        }

        const ids = restaurants.map((restaurant) => restaurant.id);
        logger.log("Fetch done", { variables, ids });
        actions.setFetchedPreviews(restaurants);
        applyDistanceOrder(actions, restaurants, getState().origin);
        actions.setLoading(false);
      } catch (error) {
        if (!abortSignal.aborted && !aborted) {
          failListInitialLoad(store);
          const fetchError =
            error instanceof Error
              ? error
              : new Error("Failed to fetch restaurants");
          logger.log("Fetch failed", { error: fetchError });
          actions.setError(fetchError);
          // Settle on failure too, or the host holds a skeleton indefinitely.
          actions.setHasSettled(true);
          actions.setLoading(false);
        }
      } finally {
        abortSignal.removeEventListener("abort", onAbort);
        if (getState().abortController === abortController) {
          actions.setAbortController(undefined);
        }
      }
    },
  ),
  refetch: thunk((actions, _payload, { getState }) => {
    const { variables, origin } = getState();
    if (!variables) {
      return;
    }
    return actions.fetch({ variables, origin });
  }),
};

export default allIdsModel;
